import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import {
  usePack,
  usePacks,
  coverUrl,
  signedPackFileUrl,
  formatBytes,
  LEVEL_LABEL,
  PROMPT_PACK_CATEGORY,
  type PackFileRow,
  type WhatYouBuildRow,
} from "@/hooks/usePacks";
import { Download, ExternalLink, Lock, ArrowLeft, Clock, Wallet, ListOrdered, Tag } from "lucide-react";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { SITE, ENTRY_PLAN } from "@/data/site";
import { cleanSummaryShort } from "@/lib/summary";

const SKOOL_URL = "https://www.skool.com/ai-automations-by-hector-8106/about";

/** Markdown styling without the typography plugin: arbitrary variants on the wrapper. */
const PROSE =
  "text-[15px] leading-7 text-foreground/90 " +
  "[&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-foreground " +
  "[&_h3]:mt-7 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground " +
  "[&_p]:my-4 [&_a]:text-primary [&_a]:underline-offset-2 hover:[&_a]:underline " +
  "[&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 " +
  "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[13px] " +
  "[&_pre]:my-5 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:border [&_pre]:border-border [&_pre]:bg-muted [&_pre]:p-4 [&_pre_code]:bg-transparent [&_pre_code]:p-0 " +
  "[&_table]:my-5 [&_table]:w-full [&_table]:text-sm [&_th]:border-b [&_th]:border-border [&_th]:py-2 [&_th]:pr-4 [&_th]:text-left [&_th]:font-semibold [&_td]:border-b [&_td]:border-border/60 [&_td]:py-2 [&_td]:pr-4 [&_td]:align-top " +
  "[&_blockquote]:my-5 [&_blockquote]:border-l-4 [&_blockquote]:border-primary/60 [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground " +
  "[&_hr]:my-8 [&_hr]:border-border";

const FileButton = ({ file }: { file: PackFileRow }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  if (file.kind === "link" && file.external_url) {
    return (
      <a href={file.external_url} target="_blank" rel="noopener noreferrer">
        <Button variant="outline" className="w-full justify-between">
          <span>{file.label}</span>
          <ExternalLink className="w-4 h-4" />
        </Button>
      </a>
    );
  }

  const open = async () => {
    if (!file.storage_path) return;
    setBusy(true);
    try {
      const url = await signedPackFileUrl(file.storage_path, 120);
      if (file.kind === "html") {
        // Storage serves HTML as text/plain on purpose (anti-phishing), so a direct open
        // would show source. Fetch it and open it as a blob on this page's origin: the
        // guides are self-contained, so they render fully and keep their localStorage.
        const tab = window.open("", "_blank");
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status} fetching the guide`);
        const html = await res.text();
        const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
        if (tab) tab.location.href = blobUrl;
        else window.location.assign(blobUrl);
      } else {
        window.location.assign(url);
      }
    } catch (e) {
      toast({
        title: "Could not open that file",
        description: (e as Error).message || "Your access could not be verified. Sign in again and retry.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const isHtml = file.kind === "html";
  return (
    <Button variant={isHtml ? "outline" : "default"} className="w-full justify-between" onClick={open} disabled={busy}>
      <span>{file.label}</span>
      <span className="flex items-center gap-2 text-xs opacity-80">
        {file.size_bytes ? formatBytes(file.size_bytes) : null}
        {isHtml ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}
      </span>
    </Button>
  );
};

const PackDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error } = usePack(slug);
  const { data: others } = usePacks();
  const { user, loading: authLoading } = useAuth();
  const { hasAccess, loading: accessLoading } = usePaymentStatus();

  // Navbar and Footer are rendered globally by App.tsx.
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-32 text-center text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-32 text-center">
          <p className="text-muted-foreground mb-6">That pack does not exist, or is not published yet.</p>
          <Link to="/packs">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4" /> All packs
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const { pack, files } = data;
  const rows = (Array.isArray(pack.what_you_build) ? pack.what_you_build : []) as WhatYouBuildRow[];
  const isPromptPack = pack.category === PROMPT_PACK_CATEGORY;
  // A prompt pack lives in the Prompt Library; everything else is a Build Pack.
  const home = isPromptPack ? { name: "Prompt Library", path: "/prompts" } : { name: "Build Packs", path: "/packs" };
  const related = (others ?? [])
    .filter((p) => p.slug !== pack.slug && (p.category === PROMPT_PACK_CATEGORY) === isPromptPack)
    .slice(0, 3);
  const gateLoading = authLoading || accessLoading;
  const canDownload = !!user && hasAccess;

  /**
   * The audit rated /packs/the-stack-you-sell 78/100 — the best page on the site, and
   * the only one with numbers, a date, a table and an honest "what was not verified"
   * section. What it lacked was any machine-readable statement of what it is. A pack is
   * a Product bundled into the membership, so the Offer points at the membership price
   * rather than inventing a per-pack one.
   */
  const packJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `${SITE.origin}/packs/${pack.slug}#product`,
      name: pack.title,
      description: pack.summary || pack.tagline || undefined,
      url: `${SITE.origin}/packs/${pack.slug}`,
      ...(pack.cover_path ? { image: coverUrl(pack.cover_path) } : {}),
      brand: { "@id": `${SITE.origin}/#organization` },
      category: pack.category ?? undefined,
      ...(pack.version ? { releaseNotes: `Version ${pack.version}` } : {}),
      isRelatedTo: { "@id": `${SITE.origin}/#course` },
      offers: {
        "@type": "Offer",
        // Included with membership; the price shown is the cheapest way to reach it.
        price: String(ENTRY_PLAN.amount),
        priceCurrency: "USD",
        url: `${SITE.origin}/#pricing`,
        availability: "https://schema.org/InStock",
        seller: { "@id": `${SITE.origin}/#organization` },
      },
      ...(pack.updated_at ? { dateModified: pack.updated_at } : {}),
    },
    breadcrumbLd([
      { name: "Home", path: "/" },
      home,
      { name: pack.title, path: `/packs/${pack.slug}` },
    ]),
  ];

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={pack.title}
        description={
          cleanSummaryShort(pack.summary || pack.tagline) ||
          `${pack.title} — a Build Pack from AI Automation Base.`
        }
        path={`/packs/${pack.slug}`}
        type="article"
        modifiedTime={pack.updated_at ?? undefined}
        author={SITE.founder}
        image={pack.cover_path ? coverUrl(pack.cover_path) : undefined}
        noindex={pack.status === "draft"}
        jsonLd={packJsonLd}
      />

      <main className="container mx-auto px-4 pt-24 pb-20 max-w-5xl">
        <Link to={home.path} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {home.name}
        </Link>

        {/* Hero */}
        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="secondary">{pack.category}</Badge>
            <span className="text-xs text-muted-foreground">{LEVEL_LABEL[pack.level] ?? pack.level}</span>
            {pack.status === "draft" ? <Badge variant="destructive">Draft — admins only</Badge> : null}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight text-foreground">{pack.title}</h1>
          <p className="mt-2 text-xl italic font-serif text-primary">{pack.tagline}</p>
          <div className="mt-5 overflow-hidden rounded-xl border border-border bg-muted aspect-[16/9]">
            <img src={coverUrl(pack.cover_path)} alt={`Cover art for the ${pack.title} Build Pack`} className="h-full w-full object-cover" />
          </div>
          <dl className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {pack.time_estimate ? (
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {pack.time_estimate}
              </div>
            ) : null}
            {pack.cost_line ? (
              <div className="flex items-center gap-1.5">
                <Wallet className="w-4 h-4" /> {pack.cost_line}
              </div>
            ) : null}
            {pack.prompts_count ? (
              <div className="flex items-center gap-1.5">
                <ListOrdered className="w-4 h-4" /> {pack.prompts_count} prompts
              </div>
            ) : null}
            <div className="flex items-center gap-1.5">
              <Tag className="w-4 h-4" /> v{pack.version}
            </div>
          </dl>
          {pack.stack.length ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {pack.stack.map((s) => (
                <Badge key={s} variant="outline" className="font-normal">
                  {s}
                </Badge>
              ))}
            </div>
          ) : null}
        </header>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Article */}
          <article>
            <p className="text-lg text-muted-foreground">{pack.summary}</p>

            {rows.length ? (
              <section className="mt-8">
                <h2 className="text-2xl font-semibold text-foreground mb-3">What you build</h2>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/60 text-left">
                      <tr>
                        <th className="px-4 py-2 font-semibold">Part</th>
                        <th className="px-4 py-2 font-semibold">What it is</th>
                        <th className="px-4 py-2 font-semibold">What it does</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} className="border-t border-border/60 align-top">
                          <td className="px-4 py-2 font-medium whitespace-nowrap">{r.part}</td>
                          <td className="px-4 py-2">{r.what}</td>
                          <td className="px-4 py-2 text-muted-foreground">{r.does}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            <div className={PROSE}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{pack.body_md}</ReactMarkdown>
            </div>
          </article>

          {/* Files — the only gated part */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground mb-1">Files</h2>
              <p className="text-xs text-muted-foreground mb-4">
                Version {pack.version}. Members of the vault and of AI Automations by Hector on Skool.
              </p>

              {gateLoading ? (
                <div className="text-sm text-muted-foreground">Checking access...</div>
              ) : canDownload ? (
                <div className="space-y-2">
                  {files.map((f) => (
                    <FileButton key={f.id} file={f} />
                  ))}
                  {files.length === 0 ? <p className="text-sm text-muted-foreground">No files attached yet.</p> : null}
                  {files.some((f) => f.sha256) ? (
                    <details className="pt-2 text-xs text-muted-foreground">
                      <summary className="cursor-pointer">Checksums</summary>
                      <ul className="mt-2 space-y-1 break-all font-mono">
                        {files
                          .filter((f) => f.sha256)
                          .map((f) => (
                            <li key={f.id}>
                              {f.label}: {f.sha256}
                            </li>
                          ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-border p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                    <Lock className="w-4 h-4 text-primary" /> Members only
                  </div>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1 mb-4">
                    {files.map((f) => (
                      <li key={f.id}>{f.label}</li>
                    ))}
                  </ul>
                  {!user ? (
                    <Link to="/auth">
                      <Button className="w-full mb-2">Sign in</Button>
                    </Link>
                  ) : null}
                  <a href="/#pricing">
                    <Button variant={user ? "default" : "outline"} className="w-full mb-2">
                      See the plans
                    </Button>
                  </a>
                  <a href={SKOOL_URL} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" className="w-full">
                      Or join on Skool, $47/month
                    </Button>
                  </a>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Skool members: sign in here with the same email and access syncs within a day.
                  </p>
                </div>
              )}
            </div>
          </aside>
        </div>

        {related.length ? (
          <section className="mt-16">
            <h2 className="text-xl font-semibold text-foreground mb-4">More packs</h2>
            <div className="grid gap-5 sm:grid-cols-3">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/packs/${p.slug}`}
                  className="rounded-lg border border-border bg-card overflow-hidden hover:border-primary/60"
                >
                  <div className="aspect-[16/9] bg-muted">
                    <img src={coverUrl(p.cover_path)} alt={p.title} loading="lazy" className="h-full w-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="text-xs text-muted-foreground mb-1">{p.category}</div>
                    <div className="font-medium text-foreground leading-snug">{p.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
};

export default PackDetail;
